import {
    mdiCheck,
    mdiEmailOutline,
    mdiMagnify,
    mdiPlus,
    mdiPriorityLow,
    mdiExclamationThick,
    mdiTrendingUp,
    mdiLightningBolt,
    mdiCircleOffOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { formatDate } from '../../services/primitives/date-utils';
import _ from 'lodash';
import * as mobx from 'mobx';
import React from 'react';
import { Row } from 'react-table';
import { MessagePurposeType } from '../../api';
import { MessagingContext } from '../../services';
import { getOptions, BaseSelect, ISelectOption, Spinner } from '../base';
import { TabContainer } from '../tab-container';
import { DataTableCell, IRowAction, ITableColumn, ITableFetchOptions } from '../data-table';
import { InfiniteScrollDataTablePresenter as ISDPresenter } from '../data-table/infinite-scroll-data-table.presenter';
import { FilterMethod, IFilterSchema, IColumnFiltersConfig } from '../data-table/components/filters/filters.models';
import { Settings } from '../data-table/components/settings/settings';
import { TableExport } from '../data-table/components/table-export/table-export';
import { AuthorDateFilter } from '../data-table/components/column-filter';

import * as S from './message-list.styles';
import { DoneButton } from './done-button';
import { UndoneButton } from './undone-button';
import { ProviderDoneButton } from './provider-done-button';
import { getCommentPreview, getLastComment, getNickname, IMessagingAuthor, IMessagingMessage } from './util';

interface IMessageListRedesignedProps {
    inboxCount: number;
    inboxMessages: IMessagingMessage[];
    doneCount: number;
    doneMessages: IMessagingMessage[];
    onFetchData: (newTableState: ITableFetchOptions, showDone?: boolean) => Promise<void>;
    onShowCompose: () => void;
    selectedMessageId: string | undefined;
    showFiltering: boolean;
    showNavigation: boolean;
    showSettings?: boolean;
    customColumns?: ITableColumn[];
    unreadCount?: number;
    /** Unread count for Done tab (when showDoneMessages). When not provided, read filter on Done tab uses unreadCount. */
    unreadDoneCount?: number;
    /** Total count for Inbox ignoring the read filter — keeps dropdown labels stable when a read filter is active. */
    allFilteredCount?: number;
    /** Total count for Done tab ignoring the read filter. */
    allDoneFilteredCount?: number;
    columnFiltersConfig?: IColumnFiltersConfig;
    rowActions?: IRowAction[];
    refreshKey?: number;
    onTabChange?: (showDone: boolean) => void;
    /** Optional loader for author options used by Created/Updated combined filter */
    loadAuthorOptions?: (searchTerm: string) => Promise<ISelectOption[]>;
    /** Optional current user ID to support "Self" checkbox for author filter */
    currentUserId?: string;
    /** Optional label for "Self" checkbox in author filter */
    currentUserLabel?: string;
}

interface IMessageListRedesignedState {
    tableState: ITableFetchOptions | undefined;
    selected: string[];
    showDoneMessages: boolean;
    isPending: boolean;
    searchTerm: string;
    lastKnownValidCount: number | null;
    isUnreadFilterActive: boolean;
    currentSortBy: any[];
    selectedRowId: string | null;
    readFilter: 'all' | 'unread' | 'read';
}

export class MessageListRedesigned extends React.Component<IMessageListRedesignedProps, IMessageListRedesignedState> {
    public static contextType = MessagingContext;
    private presenter: ISDPresenter<IMessagingMessage>;

    private get columnFiltersConfig(): IColumnFiltersConfig {
        return {
            ...(this.props.columnFiltersConfig || {}),
            ID: {
                filterType: 'String',
                filterId: 'filterId',
                placeholder: 'Search by ID...',
                defaultMethod: FilterMethod.LK,
            },
            Created: {
                filterType: 'Custom',
                component: (props: any) => (
                    <AuthorDateFilter
                        {...props}
                        loadOptions={this.props.loadAuthorOptions}
                        currentUserId={this.props.currentUserId}
                        currentUserLabel={this.props.currentUserLabel}
                    />
                ),
                filterId: 'filterCreatedCombined',
                label: 'Filter Created',
                defaultMethod: FilterMethod.LK,
            },
            Updated: {
                filterType: 'DateTime',
                filterId: 'filterUpdated',
                label: 'Filter Updated',
                defaultMethod: FilterMethod.GTE,
            },
        };
    }

    private get columns(): ITableColumn<IMessagingMessage>[] {
        return [
            {
                id: 'ID',
                Header: 'ID',
                accessor: 'id',
                minWidth: 100,
                maxWidth: 300,
                show: false,
            },
            {
                id: 'Created',
                Header: 'Created',
                accessor: 'created',
                Cell: ({ row, cell }): React.ReactElement => {
                    const { author } = row.original;
                    let formattedDate = '—';

                    const dateValue = cell.value;
                    if (dateValue) {
                        const parsedDate = new Date(dateValue);
                        if (!isNaN(parsedDate.getTime())) {
                            formattedDate = formatDate(parsedDate, 'MMM d, yyyy, h:mm a');
                        }
                    }

                    return (
                        <S.NameDateCellContainer>
                            <span>{getNickname(author, true) || 'Unknown'}</span>
                            <span>{formattedDate}</span>
                        </S.NameDateCellContainer>
                    );
                },
                minWidth: 150,
                maxWidth: 400,
                show: true,
            },
            {
                id: 'Updated',
                Header: 'Updated',
                accessor: 'updated',
                Cell: ({ row, cell }): React.ReactElement => {
                    const { lastAuthor } = row.original;
                    let formattedDate = '—';

                    const dateValue = cell.value;
                    if (dateValue) {
                        const parsedDate = new Date(dateValue);
                        if (!isNaN(parsedDate.getTime())) {
                            formattedDate = formatDate(parsedDate, 'MMM d, yyyy, h:mm a');
                        }
                    }

                    return (
                        <S.NameDateCellContainer>
                            <span>{getNickname(lastAuthor, true) || 'Unknown'}</span>
                            <span>{formattedDate}</span>
                        </S.NameDateCellContainer>
                    );
                },
                minWidth: 150,
                maxWidth: 400,
                defaultSort: { id: 'Updated', desc: true },
                show: true,
            },
            {
                id: 'Subject',
                Header: 'Subject',
                Cell: ({ row }): React.ReactElement => {
                    const { documents, subject, read } = row.original;
                    const lastCommentPreview = getCommentPreview(getLastComment(row.original)?.body);

                    return (
                        <S.NameDateCellContainer>
                            <S.SubjectRow>
                                <S.SubjectText unread={!read}>{subject}</S.SubjectText>
                                {!!documents && documents > 0 && <S.AttachmentField count={documents} />}
                            </S.SubjectRow>
                            {lastCommentPreview && <S.SubjectPreview>{lastCommentPreview}</S.SubjectPreview>}
                        </S.NameDateCellContainer>
                    );
                },
                accessor: 'subject',
                minWidth: 300,
                maxWidth: 2000,
            },
            {
                id: 'Priority',
                Header: 'Priority',
                accessor: row => {
                    const priority = row.priority?.toUpperCase() || '';
                    return priority || 'NONE';
                },
                Cell: ({ row }): React.ReactElement => {
                    const priority = (row.original as any).priority;
                    const priorityUpper = priority?.toUpperCase();

                    let priorityIconPath: string = mdiCircleOffOutline;
                    let priorityLabel = 'None';

                    if (priorityUpper && priorityUpper !== '-') {
                        switch (priorityUpper) {
                            case 'LOW':
                                priorityIconPath = mdiPriorityLow;
                                priorityLabel = 'Low';
                                break;
                            case 'NORMAL':
                                priorityIconPath = mdiTrendingUp;
                                priorityLabel = 'Normal';
                                break;
                            case 'HIGH':
                                priorityIconPath = mdiLightningBolt;
                                priorityLabel = 'High';
                                break;
                            case 'URGENT':
                                priorityIconPath = mdiExclamationThick;
                                priorityLabel = 'Urgent';
                                break;
                        }
                    }

                    return (
                        <S.PriorityTag priority={priorityUpper || ''}>
                            <S.PriorityTagIcon>
                                <Icon path={priorityIconPath} size={0.65} />
                            </S.PriorityTagIcon>
                            <S.PriorityTagLabel>{priorityLabel}</S.PriorityTagLabel>
                        </S.PriorityTag>
                    );
                },
                minWidth: 90,
                maxWidth: 180,
                sortType: 'PRIORITY',
            },
            {
                id: 'Purpose',
                Header: 'Purpose',
                accessor: 'purposeType',
                Cell: DataTableCell('String', { normalized: true, type: 'String' }),
                minWidth: 180,
                maxWidth: 400,
            },
            // @ts-expect-error Custom columns
            ...(this.props.customColumns || []),
        ];
    }

    private filterSchemas: IFilterSchema[] = [
        {
            label: 'ID',
            filterId: 'filterId',
            filterType: 'String',
            columnId: 'ID',
        },
        {
            label: 'Subject',
            filterId: 'filterSubject',
            filterType: 'String',
            columnId: 'Subject',
        },
        {
            label: 'Author',
            filterId: 'filterAuthor',
            filterType: 'String',
            columnId: 'Created',
        },
        {
            label: 'Updated',
            filterId: 'filterUpdated',
            filterType: 'DateTime',
            columnId: 'Updated',
        },
        {
            label: 'Created',
            filterId: 'filterCreated',
            filterType: 'DateTime',
            columnId: 'Created',
        },
        {
            label: 'Read',
            filterId: 'filterRead',
            filterType: 'Select',
            options: [
                {
                    label: 'Yes',
                    value: '1',
                },
                {
                    label: 'No',
                    value: '0',
                },
            ],
            columnId: '',
        },
        {
            label: 'Purpose',
            filterId: 'filterPurpose',
            filterType: 'Select',
            options: getOptions(MessagePurposeType, { normalize: true }),
            columnId: 'purpose',
        },
    ];

    constructor(props: IMessageListRedesignedProps) {
        super(props);
        const tableDefaults = this.getInitialTableDefaults();
        this.state = {
            tableState: undefined,
            selected: [],
            showDoneMessages: false,
            isPending: false,
            searchTerm: '',
            lastKnownValidCount: null,
            isUnreadFilterActive: false,
            currentSortBy: tableDefaults?.sortBy || [],
            selectedRowId: props.selectedMessageId || null,
            readFilter: 'all',
        };
        this.presenter = new ISDPresenter<IMessagingMessage>({
            name: 'message-list-redesigned',
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        });

        // Observe presenter's selectedIds and sync with component state
        mobx.reaction(
            () => this.presenter.selectedIds,
            selectedIds => {
                const selectedArray = Object.keys(selectedIds).filter(id => selectedIds[id]);
                this.setState({ selected: selectedArray });
            },
        );
    }

    private getInitialTableDefaults = () => {
        return new ISDPresenter<IMessagingMessage>({
            name: 'message-list-redesigned',
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        }).getTableDefaults();
    };

    private handleFetchData = async (tableState: ITableFetchOptions): Promise<void> => {
        if (tableState.sortBy) {
            this.setState({ currentSortBy: tableState.sortBy });
        }
        return this.props.onFetchData(tableState, this.state.showDoneMessages);
    };

    public componentDidUpdate(prevProps: IMessageListRedesignedProps, prevState: IMessageListRedesignedState): void {
        const { showDoneMessages, searchTerm, lastKnownValidCount, currentSortBy, readFilter } = this.state;
        const { doneCount, doneMessages, inboxCount, inboxMessages, selectedMessageId, unreadCount, unreadDoneCount } =
            this.props;

        if (prevProps.selectedMessageId !== selectedMessageId) {
            this.setState({ selectedRowId: selectedMessageId || null });
        }

        const hasReadFilter = !!_.find(this.presenter.filters, { filterId: 'filterRead' });
        if (hasReadFilter !== this.state.isUnreadFilterActive) {
            this.setState({ isUnreadFilterActive: hasReadFilter });
        }

        if (!hasReadFilter && this.state.readFilter !== 'all') {
            this.setState({ readFilter: 'all' });
        }

        // Reset accumulated data when switching tabs
        if (prevState.showDoneMessages !== showDoneMessages) {
            this.presenter.resetData();
            this.setState({ lastKnownValidCount: null });
            return;
        }

        // Reset accumulated data when sorting changes
        if (!_.isEqual(prevState.currentSortBy, currentSortBy)) {
            mobx.runInAction(() => {
                this.presenter.resetData();
            });
            this.setState({ lastKnownValidCount: null });

            return;
        }

        // Reset accumulated data when refreshKey changes (full refresh requested)
        if (this.props.refreshKey !== undefined && prevProps.refreshKey !== this.props.refreshKey) {
            mobx.runInAction(() => {
                this.presenter.resetData();
            });
            this.setState({ lastKnownValidCount: null });
        }

        const visibleMessages = showDoneMessages ? doneMessages : inboxMessages;
        const visibleCount = showDoneMessages ? doneCount : inboxCount;
        const prevVisibleCount = showDoneMessages ? prevProps.doneCount : prevProps.inboxCount;
        const prevMessages = showDoneMessages ? prevProps.doneMessages : prevProps.inboxMessages;

        // Check if read filter is being sent to API (server-side filtering)
        const hasApiReadFilter = !!_.find(this.presenter.filters, { filterId: 'filterRead' });

        const filterByRead = (msgs: IMessagingMessage[]): IMessagingMessage[] => {
            // Don't do client-side filtering if API is already filtering
            if (hasApiReadFilter) return msgs;
            if (readFilter === 'unread') return msgs.filter(m => !(m as any).read);
            if (readFilter === 'read') return msgs.filter(m => (m as any).read);
            return msgs;
        };
        const filteredVisibleMessages = filterByRead(visibleMessages);
        const prevFilteredMessages = filterByRead(prevMessages);

        // Use allFilteredCount for reset detection to avoid resetting when read filter changes the count
        // This is the total count ignoring the read filter
        const countForResetDetection = showDoneMessages
            ? (this.props.allDoneFilteredCount ?? doneCount)
            : (this.props.allFilteredCount ?? inboxCount);
        const prevCountForResetDetection = showDoneMessages
            ? (prevProps.allDoneFilteredCount ?? prevProps.doneCount)
            : (prevProps.allFilteredCount ?? prevProps.inboxCount);

        const effectiveVisibleCount = visibleCount;
        const prevEffectiveVisibleCount = prevVisibleCount;
        const messagesForTable = readFilter === 'all' ? visibleMessages : filteredVisibleMessages;
        const prevMessagesForTable = readFilter === 'all' ? prevMessages : prevFilteredMessages;

        if (countForResetDetection !== prevCountForResetDetection && this.presenter.accumulatedData.length > 0) {
            mobx.runInAction(() => {
                this.presenter.resetData();
            });
            this.setState({ lastKnownValidCount: null });
        }

        if (
            lastKnownValidCount !== null &&
            visibleCount === lastKnownValidCount &&
            this.presenter.accumulatedData.length === 0
        ) {
            return;
        }

        // Compare by content (message IDs) - use filtered lists when read filter is active
        const getMessageIds = (msgs: IMessagingMessage[]): string => msgs.map(m => m.id).join(',');
        const prevIds = getMessageIds(prevMessagesForTable);
        const currentIds = getMessageIds(messagesForTable);
        const idsChanged = prevIds !== currentIds;

        // Check if any message content changed (updated, read, open) for in-place updates and refilter
        const getMessageContentSig = (msgs: IMessagingMessage[]): string =>
            msgs.map(m => `${m.id}:${m.updated || ''}:${(m as any).read}:${(m as any).open}`).join(',');
        const prevSig = getMessageContentSig(prevMessagesForTable);
        const currentSig = getMessageContentSig(messagesForTable);
        const contentChanged = prevSig !== currentSig;

        // Check if selected message object reference changed (in-place update via replaceInArray)
        const currentSelectedMessageId = this.props.selectedMessageId;
        let selectedMessageObjectChanged = false;
        if (
            currentSelectedMessageId &&
            prevMessagesForTable.length === messagesForTable.length &&
            prevMessagesForTable.length > 0 &&
            !idsChanged
        ) {
            const prevSelectedIndex = prevMessagesForTable.findIndex(m => m.id === currentSelectedMessageId);
            const currentSelectedIndex = messagesForTable.findIndex(m => m.id === currentSelectedMessageId);
            if (prevSelectedIndex !== -1 && currentSelectedIndex !== -1 && prevSelectedIndex === currentSelectedIndex) {
                if (prevMessagesForTable[prevSelectedIndex] !== messagesForTable[currentSelectedIndex]) {
                    selectedMessageObjectChanged = true;
                }
            }
        }

        const messagesChanged = idsChanged || contentChanged || selectedMessageObjectChanged;

        const needsInitialPopulation = this.presenter.accumulatedData.length === 0 && messagesForTable.length > 0;

        if (!messagesChanged && !needsInitialPopulation) {
            return;
        }

        const isPagination = idsChanged && visibleMessages.length > prevMessages.length;
        const isInPlaceUpdate =
            !idsChanged &&
            (contentChanged || selectedMessageObjectChanged) &&
            effectiveVisibleCount === prevEffectiveVisibleCount &&
            messagesForTable.length === prevMessagesForTable.length &&
            messagesForTable.length > 0;

        if (messagesForTable.length > 0) {
            mobx.runInAction(() => {
                if (needsInitialPopulation) {
                    this.presenter.onDataUpdate(messagesForTable);
                    this.setState({ lastKnownValidCount: null });
                } else if (isPagination) {
                    this.presenter.appendData(messagesForTable);
                } else if (isInPlaceUpdate) {
                    if (readFilter !== 'all') {
                        // Only reset if message count changed (message removed from filter results)
                        // Otherwise, just update items in place to preserve pagination
                        const shouldReset = messagesForTable.length !== this.presenter.accumulatedData.length;

                        if (shouldReset) {
                            this.presenter.resetData();
                            this.presenter.onDataUpdate(messagesForTable);
                            this.setState({ lastKnownValidCount: null });
                        } else {
                            messagesForTable.forEach(msg => this.presenter.updateItem(msg));
                        }
                    } else {
                        messagesForTable.forEach(msg => this.presenter.updateItem(msg));
                    }
                } else if (messagesChanged) {
                    this.presenter.resetData();
                    this.presenter.onDataUpdate(messagesForTable);
                    this.setState({ lastKnownValidCount: null });
                }

                // When read filter is active, the API is already filtering, so use effectiveVisibleCount
                // (which comes from the filtered API response) for hasMore calculation.
                // The unread count props are used for filter dropdown labels only.
                const actualCount = effectiveVisibleCount;

                const hasMore = this.presenter.accumulatedData.length < actualCount;

                this.presenter.setHasMore(hasMore);
                this.presenter.onDataTotalChange(effectiveVisibleCount);

                // If we've loaded all data, ensure loading state is cleared
                if (!hasMore) {
                    this.presenter.setIsLoadingMore(false);
                }
            });
        } else {
            mobx.runInAction(() => {
                // Empty response - check if we have accumulated data
                if (this.presenter.accumulatedData.length > 0) {
                    // We have data but received 0 new items - we've reached the end
                    this.presenter.setHasMore(false);
                    this.presenter.setIsLoadingMore(false);
                    this.presenter.onDataTotalChange(effectiveVisibleCount);
                } else {
                    // No data at all - reset everything
                    this.presenter.resetData();
                    this.presenter.setHasMore(false);
                    this.presenter.onDataTotalChange(effectiveVisibleCount);
                }
            });
            this.setState({ lastKnownValidCount: null });
        }
    }

    public componentWillUnmount(): void {
        // Cancel pending debounced calls to prevent memory leaks
        this.updateSubjectFilter.cancel();
    }

    private getReadFilterOptions = (): ISelectOption[] => {
        const { showDoneMessages } = this.state;

        // Use the read-filter-independent totals so the counts remain stable
        // regardless of which read filter is currently selected. Fall back to
        // inboxCount/doneCount if the parent hasn't wired up the new props yet.
        const totalCount = showDoneMessages
            ? (this.props.allDoneFilteredCount ?? this.props.doneCount)
            : (this.props.allFilteredCount ?? this.props.inboxCount);
        const unreadForTab = showDoneMessages ? (this.props.unreadDoneCount ?? 0) : (this.props.unreadCount ?? 0);

        const readCount = totalCount - unreadForTab;

        const isListLoading = (this.context as any)?.isListLoading;

        if (isListLoading) {
            return [
                { value: 'all', label: 'Show all (…)' },
                { value: 'unread', label: 'Show unread (…)' },
                { value: 'read', label: 'Show read (…)' },
            ];
        }

        return [
            { value: 'all', label: `Show all (${totalCount})` },
            { value: 'unread', label: `Show unread (${unreadForTab})` },
            { value: 'read', label: `Show read (${readCount})` },
        ];
    };

    private handleReadFilterChange = (newValue: ISelectOption | readonly ISelectOption[] | null): void => {
        if (Array.isArray(newValue) || !newValue) return;

        const option = newValue as ISelectOption;
        const value = (option.value || 'all') as 'all' | 'unread' | 'read';
        this.setState({ readFilter: value });

        if (value === 'unread') {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterRead' },
                {
                    filterId: 'filterRead',
                    filterType: 'Select',
                    filterMethod: FilterMethod.EQ,
                    value: { label: 'No', value: '0' },
                },
            );
        } else if (value === 'read') {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterRead' },
                {
                    filterId: 'filterRead',
                    filterType: 'Select',
                    filterMethod: FilterMethod.EQ,
                    value: { label: 'Yes', value: '1' },
                },
            );
        } else {
            this.presenter.findAndRemoveFilter({ filterId: 'filterRead' });
        }
    };

    private renderCustomToolbar = (
        rows: Row<IMessagingMessage>[],
        prepareRow: (row: Row<IMessagingMessage>) => void,
    ): React.ReactElement => {
        const { selected, showDoneMessages, isPending, searchTerm, readFilter } = this.state;
        const readFilterOptions = this.getReadFilterOptions();
        const isListLoading = (this.context as any)?.isListLoading;

        return (
            <S.TableHeaderSection>
                <S.SearchSection>
                    <S.SearchInputWrapper>
                        <S.SearchIcon>
                            <Icon path={mdiMagnify} size={0.85} />
                        </S.SearchIcon>
                        <S.SearchInput
                            type="text"
                            placeholder="Search messages"
                            value={searchTerm}
                            onChange={this.handleSearchChange}
                        />
                    </S.SearchInputWrapper>
                </S.SearchSection>

                <S.ToolbarActions>
                    <S.ReadFilterSelectWrapper>
                        <S.ReadFilterInner>
                            <BaseSelect
                                options={readFilterOptions}
                                value={readFilterOptions.find(opt => opt.value === readFilter)}
                                onChange={this.handleReadFilterChange}
                                isSearchable={false}
                                placeholder="Filter messages"
                                required={true}
                                isDisabled={isListLoading}
                            />
                            {isListLoading && (
                                <S.ReadFilterLoader>
                                    <Spinner size="16px" />
                                </S.ReadFilterLoader>
                            )}
                        </S.ReadFilterInner>
                    </S.ReadFilterSelectWrapper>

                    <Settings
                        columns={this.columns}
                        hiddenColumnIds={this.presenter.hiddenColumnIds}
                        onColumnToggle={this.presenter.onColumnToggle}
                        pageSize={this.presenter.controlledPageSize}
                        onPageSizeChange={this.presenter.onPageSizeUpdate}
                        pageSizeOptions={[10, 20, 30, 50, 100]}
                        variant="small"
                    />

                    <TableExport rows={rows} prepareRow={prepareRow} />
                </S.ToolbarActions>
            </S.TableHeaderSection>
        );
    };

    public render(): React.ReactElement {
        const { showDoneMessages } = this.state;
        const { doneCount, inboxCount, unreadCount, unreadDoneCount, onShowCompose } = this.props;

        // When filtering by unread, show unread counts in tab labels so they update after mark as read
        const showUnreadInTabs = this.state.readFilter === 'unread';
        const inboxLabel = `Inbox (${showUnreadInTabs ? (unreadCount ?? inboxCount) : inboxCount})`;
        const doneLabel = `Done (${showUnreadInTabs ? (unreadDoneCount ?? doneCount) : doneCount})`;

        return (
            <S.MessageListContainer>
                <S.MessageHeader>
                    <S.HeaderTop>
                        <S.Title>Tasks</S.Title>
                        <S.HeaderActions>
                            {this.state.selected.length > 0 && !this.state.showDoneMessages && (
                                <>
                                    <DoneButton
                                        count={this.state.selected.length}
                                        disabled={this.state.isPending || (this.context as any).isReadOnly}
                                        onClick={this.markSelectedDone}
                                    />
                                    {(this.context as any).onProviderMarkAsClosed && (
                                        <span style={{ marginLeft: 8 }}>
                                            <ProviderDoneButton
                                                count={this.state.selected.length}
                                                disabled={this.state.isPending || (this.context as any).isReadOnly}
                                                onClick={this.markSelectedProviderDone}
                                            />
                                        </span>
                                    )}
                                </>
                            )}
                            {this.state.selected.length > 0 && this.state.showDoneMessages && (
                                <UndoneButton
                                    count={this.state.selected.length}
                                    disabled={this.state.isPending || (this.context as any).isReadOnly}
                                    onClick={this.markSelectedUndone}
                                />
                            )}
                            <S.PrimaryButton disabled={(this.context as any).isReadOnly} onClick={onShowCompose}>
                                <Icon path={mdiPlus} size={0.7} />
                                <span>New task</span>
                            </S.PrimaryButton>
                        </S.HeaderActions>
                    </S.HeaderTop>
                </S.MessageHeader>

                {/* Table Card */}
                <S.TableCard>
                    <S.TableTabSection>
                        <TabContainer
                            tabs={[
                                { id: 'inbox', label: inboxLabel, icon: mdiEmailOutline },
                                { id: 'done', label: doneLabel, icon: mdiCheck },
                            ]}
                            activeTab={showDoneMessages ? 'done' : 'inbox'}
                            onTabChange={this.handleTabChange}
                        />
                    </S.TableTabSection>

                    <S.MessagingTable
                        presenter={this.presenter}
                        isLoading={(this.context as any).isListLoading}
                        onFetch={this.handleFetchData}
                        onRowClick={this.onRowClick()}
                        rowClass={this.rowClass()}
                        hideFiltering={true}
                        hideNavigation={true}
                        hideSettings={true}
                        showExport={false}
                        selectableRows={true}
                        columnFiltersConfig={this.columnFiltersConfig}
                        renderToolbar={this.renderCustomToolbar}
                        rowActions={this.props.rowActions}
                        selectedRowId={this.state.selectedRowId}
                        onSelectedRowChange={rowId => this.setState({ selectedRowId: rowId })}
                    />
                </S.TableCard>

                <S.SelectedMessageRow show={!!this.props.selectedMessageId} />
            </S.MessageListContainer>
        );
    }

    private get hasFilter() {
        return !!_.find(this.presenter.filters, { filterId: 'filterRead' });
    }

    private rowClass(): (row: Row<IMessagingMessage>) => string {
        return (row: Row<IMessagingMessage>): string => {
            const classes: string[] = [];

            if (row.original.id === this.props.selectedMessageId) {
                classes.push('selected-message');
            }

            if (!row.original.read) {
                classes.push('unread-message');
            }

            return classes.join(' ');
        };
    }

    private onRowClick(): (row: Row) => Promise<void> {
        const { onMessageClose, onMessageOpen } = this.context as any;
        const { selectedMessageId } = this.props;
        return async (row: Row<IMessagingMessage>): Promise<void> => {
            const { original: message } = row;
            const id = message.id;
            if (id === selectedMessageId) {
                await onMessageClose();
            } else {
                // Pass the full message object instead of just the ID
                await (onMessageOpen && onMessageOpen(message));
            }
        };
    }

    private markSelectedDone = async (): Promise<void> => {
        this.setState({ isPending: true });
        try {
            if (_.includes(this.state.selected, this.props.selectedMessageId)) {
                await (this.context as any).onMessageClose();
            }
            await (this.context as any).onMarkAsClosed(this.state.selected);
            this.presenter.onSelectedUpdate({});
            this.setState({
                isPending: false,
                selected: [],
            });
        } catch (err) {
            this.setState({ isPending: false });
        }
    };
    private markSelectedUndone = async (): Promise<void> => {
        this.setState({ isPending: true });
        try {
            await (this.context as any).onMarkAsOpen(this.state.selected);
            this.presenter.onSelectedUpdate({});
            this.setState({
                isPending: false,
                selected: [],
            });
        } catch (err) {
            this.setState({ isPending: false });
        }
    };

    private markSelectedProviderDone = async (): Promise<void> => {
        const context: any = this.context as any;
        if (!context.onProviderMarkAsClosed) {
            return;
        }
        this.setState({ isPending: true });
        try {
            if (_.includes(this.state.selected, this.props.selectedMessageId)) {
                await context.onMessageClose();
            }
            const ids = _.concat([], this.state.selected);
            await Promise.all(
                _.map(ids, async (id: string) => {
                    await context.onProviderMarkAsClosed(id);
                }),
            );
            this.presenter.onSelectedUpdate({});
            this.setState({
                isPending: false,
                selected: [],
            });
        } catch (err) {
            this.setState({ isPending: false });
        }
    };

    private isSelected(id: string): boolean {
        return _.includes(this.state.selected, id);
    }

    private toggleSelected(id: string): () => void {
        return (): void => {
            if (this.isSelected(id)) {
                this.setState({ selected: _.without(this.state.selected, id) });
            } else {
                this.setState({ selected: _.concat(this.state.selected, id) });
            }
        };
    }

    private handleTabChange = (tabId: string): void => {
        const newValue = tabId === 'done';
        if (newValue !== this.state.showDoneMessages) {
            this.setState({ showDoneMessages: newValue, selected: [], readFilter: 'all' });

            // Notify parent of tab change
            this.props.onTabChange?.(newValue);

            this.presenter.onSelectedUpdate({});
            this.presenter.findAndRemoveFilter({ filterId: 'filterRead' });
            this.presenter.resetData();

            this.props.onFetchData(
                {
                    pageIndex: 0,
                    pageSize: this.presenter.controlledPageSize,
                    sortBy: this.state.currentSortBy,
                    validFilters: [
                        ...(this.presenter.validFilters || []),
                        {
                            filterId: 'filterOpen',
                            filterMethod: FilterMethod.EQ,
                            filterType: 'String',
                            value: newValue ? '0' : '1',
                        },
                    ],
                },
                newValue,
            );
        }
    };

    private updateSubjectFilter = _.debounce((value: string): void => {
        const currentCount = this.state.showDoneMessages ? this.props.doneCount : this.props.inboxCount;
        this.setState({ lastKnownValidCount: currentCount });

        if (value.trim()) {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterSubject' },
                {
                    filterId: 'filterSubject',
                    filterType: 'String',
                    filterMethod: FilterMethod.LK,
                    value: value.trim(),
                },
            );
        } else {
            this.presenter.findAndRemoveFilter({ filterId: 'filterSubject' });
        }
    }, 500);

    private handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        this.setState({ searchTerm: value });
        this.updateSubjectFilter(value);
    };
}
